/* eslint-disable react/no-danger */
// SAFETY NOTE: Every dangerouslySetInnerHTML in this file injects ONLY a
// JSON.stringify() of a server-controlled object literal (no user input
// touches it). This is the standard Google-recommended way to inject
// JSON-LD structured data for SEO. The eslint rule is therefore disabled
// for this file with a clear rationale.
import React from 'react';

export const SEOHead = ({ title, description, keywords, url, type = 'website' }) => {
  React.useEffect(() => {
    document.title = title || 'HumanOnRent.AI - India\'s First AI-Driven Human on Rent Platform';
    
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') || name.startsWith('article:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
    setMeta('author', 'HumanOnRent.AI - Set India Business Pvt Ltd');
    
    // Open Graph
    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:type', type);
    setMeta('og:url', url || window.location.href);
    setMeta('og:site_name', 'HumanOnRent.AI');
    setMeta('og:locale', 'en_IN');
    
    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    
    // AEO - AI Engine Optimization
    setMeta('ai:description', description);
    setMeta('ai:category', 'Human Services Marketplace');
    
    // GEO
    setMeta('geo.region', 'IN');
    setMeta('geo.placename', 'India');
    setMeta('geo.position', '20.5937;78.9629');
    setMeta('ICBM', '20.5937, 78.9629');
  }, [title, description, keywords, url, type]);

  return null;
};

// Schema.org structured data for EEAT
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "HumanOnRent.AI",
    "alternateName": "Human On Rent",
    "url": "https://humanonrent.ai",
    "description": "India's First AI-Driven Human on Rent Platform. 1 Lac+ verified humans for hire across all Indian cities.",
    "foundingDate": "2024",
    "parentOrganization": {
      "@type": "Organization",
      "name": "Set India Business Pvt Ltd",
      "taxID": ""
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-98105-02313",
      "contactType": "customer service",
      "email": "humanonrent@gmail.com",
      "availableLanguage": ["English", "Hindi"],
      "areaServed": "IN"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "sameAs": [],
    "knowsAbout": ["human rental services", "event partners", "travel buddies", "elder care", "professional services"],
    "numberOfEmployees": { "@type": "QuantitativeValue", "value": "100000+" },
    "slogan": "India's First AI-Driven Human on Rent Platform"
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "HumanOnRent.AI",
    "url": "https://humanonrent.ai",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://humanonrent.ai/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "description": "Rent verified humans for any occasion across India. AI-powered matching for events, travel, medical visits, elder care, and 20+ services.",
    "publisher": {
      "@type": "Organization",
      "name": "Set India Business Pvt Ltd"
    }
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

export const ServiceSchema = ({ services }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Human on Rent Services",
    "description": "20+ professional human rental services across India",
    "numberOfItems": services?.length || 20,
    "itemListElement": (services || []).map((s, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Service",
        "name": s.name || s,
        "description": s.description || `${s.name || s} service on HumanOnRent.AI`,
        "provider": { "@type": "Organization", "name": "HumanOnRent.AI" },
        "areaServed": "IN",
        "offers": {
          "@type": "Offer",
          "price": "1000",
          "priceCurrency": "INR",
          "priceSpecification": { "@type": "UnitPriceSpecification", "unitText": "hour" }
        }
      }
    }))
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (faqs || []).map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

export const LocalBusinessSchema = ({ city, providers }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `HumanOnRent.AI - ${city}`,
    "description": `Rent verified humans in ${city}. ${providers} providers available for events, travel, elder care, and 20+ services.`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressCountry": "IN"
    },
    "url": `https://humanonrent.ai/cities/${city.toLowerCase()}`,
    "telephone": "+91-98105-02313",
    "parentOrganization": { "@type": "Organization", "name": "HumanOnRent.AI" },
    "areaServed": city,
    "priceRange": "Rs.1000/hr+"
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
};

// SEO Keywords constants
export const SEO_KEYWORDS = {
  home: "human on rent, humanonrent.ai, rent a human india, boyfriend on rent, girlfriend on rent, rent a friend india, hire human near me, human rental service india, ai human matching platform, event partner rent, travel buddy india, elder care partner, human on rent near me, rent a person india",
  services: "human on rent services, hire human for movies, shopping partner rent, travel buddy on rent, medical help partner, elder care on rent, dinner partner hire, sports partner india, cooking partner rent, tutoring partner india, party partner hire, business event partner",
  cities: "human on rent mumbai, human on rent delhi, human on rent bangalore, human on rent hyderabad, human on rent chennai, human on rent kolkata, human on rent pune, human on rent ahmedabad, rent a human near me, hire human in my city",
  about: "humanonrent.ai about, set india business pvt ltd, ai driven human platform, verified human rental, safe human hire platform, india human marketplace, trusted human rental",
  provider: "earn money humanonrent, become provider humanonrent, rent yourself earn, earn 1 lac per month, flexible earning india, side income india, gig economy india, freelance human services",
  faq: "human on rent faq, how to rent a human, is human on rent safe, humanonrent pricing, human rental cost india, book human online india"
};
